import type { GlobalEvent } from '~/types/global-events'
import type { EventEmitter } from '~common/utils/events'
import type { ErrorTrackerServiceInterface } from './types'
import * as Sentry from '@sentry/react'

export interface ErrorTrackerServiceDeps {
    globalEvents: EventEmitter<GlobalEvent>
    shouldLog: boolean
}

export class InMemoryErrorTrackerService
    implements ErrorTrackerServiceInterface
{
    errors: { error: unknown; context?: Record<string, unknown> }[] = []

    constructor(private readonly deps: ErrorTrackerServiceDeps) {}

    private log(errorMsg: string, error: unknown) {
        if (!this.deps.shouldLog) {
            return
        }

        console.warn('ERROR TRACKED:', errorMsg)
        console.error(error)
    }

    trackError: ErrorTrackerServiceInterface['trackError'] = (
        errorMessage,
        error,
        context,
    ) => {
        this.errors.push({ error, context })
        this.log(errorMessage, error)

        // Report to Sentry for external monitoring
        Sentry.captureException(error, {
            tags: { message: errorMessage },
            extra: context,
        })
    }

    throwErrorWithNotification: ErrorTrackerServiceInterface['throwErrorWithNotification'] =
        (message, originalError, errorId) => {
            // Track the error first
            this.trackError(message, originalError ?? new Error(message))
            // Show error notification
            this.deps.globalEvents.emit({
                type: 'showError',
                message,
                id: errorId,
            })

            // Throw the error
            if (originalError instanceof Error) {
                throw originalError
            } else if (typeof originalError === 'string') {
                throw new Error(originalError)
            } else {
                throw new Error(message)
            }
        }

    showErrorNotification: ErrorTrackerServiceInterface['showErrorNotification'] =
        (message, errorId) => {
            // Track the notification (but not as an error)
            this.trackError(`Notification: ${message}`, new Error(message), {
                type: 'notification',
                level: 'info',
            })

            // Show error notification
            this.deps.globalEvents.emit({
                type: 'showError',
                message,
                id: errorId,
            })
        }
}
